import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page,
  Card,
  Badge,
  Button,
  EmptyState,
  Spinner,
  Banner,
  BlockStack,
  Text,
  Box,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import { getGames, type Game } from "../api/campaigns";
import { isShopInstallRedirectError } from "../api/admin-api-client";
import {
  GameCard,
  GameCatalogPreview,
  GameCatalogSection,
  groupGamesByCatalogFamily,
  WHEEL_CATALOG_TEMPLATES,
  WheelsOfFortuneFamilyCard,
} from "../components/games";
import { fetchWheelTemplateDefaults } from "../api/wheel-templates";
import type { WheelTemplatePreviewColors } from "../types/wheel";

const WHEEL_GAME_KEY = "wheel_of_fortune";

/** Only mount catalog previews when a game has a visual (avoids empty preview chrome). */
function gameCatalogPreviewFor(game: Game) {
  if (game.key === "wheel_of_fortune" || game.key === "scratch_card") {
    return <GameCatalogPreview game={game} />;
  }
  return undefined;
}

function GamesCatalogPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wheelPreviewColorsByTemplate, setWheelPreviewColorsByTemplate] = useState<
    Record<string, WheelTemplatePreviewColors> | null
  >(null);

  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGames();
      setGames(data);
      // After games load, fetch wheel template preview colors (same auth path).
      // Sequencing avoids concurrent adminApiRequest + waitForAppBridge with getGames on mount.
      try {
        const results = await Promise.all(
          WHEEL_CATALOG_TEMPLATES.map((t) => fetchWheelTemplateDefaults(t.id))
        );
        const m: Record<string, WheelTemplatePreviewColors> = {};
        WHEEL_CATALOG_TEMPLATES.forEach((t, i) => {
          m[t.id] = results[i].previewColors;
        });
        setWheelPreviewColorsByTemplate(m);
      } catch {
        setWheelPreviewColorsByTemplate({});
      }
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load games");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const { sections, unmappedGames } = useMemo(() => groupGamesByCatalogFamily(games), [games]);

  const getStatusBadge = (status: Game["status"]) => {
    switch (status) {
      case "ACTIVE":
        return <Badge tone="success">Available</Badge>;
      case "HIDDEN":
        return <Badge tone="warning">Hidden</Badge>;
      case "DEPRECATED":
        return <Badge tone="info">Deprecated</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const renderGameCard = (game: Game) => {
    if (game.key === WHEEL_GAME_KEY) {
      return (
        <WheelsOfFortuneFamilyCard
          key={game.id}
          game={game}
          statusBadge={getStatusBadge(game.status)}
          templates={WHEEL_CATALOG_TEMPLATES}
          previewColorsByTemplate={wheelPreviewColorsByTemplate}
          onUseInCampaign={(templateId) =>
            navigate(`/campaigns/new?gameId=${game.id}&wheelTemplate=${templateId}`)
          }
        />
      );
    }
    return (
      <GameCard
        key={game.id}
        game={game}
        statusBadge={getStatusBadge(game.status)}
        preview={gameCatalogPreviewFor(game)}
        onUseInCampaign={() => navigate(`/campaigns/new?gameId=${game.id}`)}
      />
    );
  };

  if (loading) {
    return (
      <Page title="Games">
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Spinner size="large" />
              <Text as="p" variant="bodyMd" tone="subdued">
                Loading games...
              </Text>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Games">
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Text as="p">Unable to load games. Please try again.</Text>
              <Button onClick={loadGames}>Retry</Button>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (games.length === 0) {
    return (
      <Page title="Games">
        <Card>
          <EmptyState
            heading="No games available"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Games will appear here once they are registered in the system.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Games"
      subtitle={`${games.length} game${games.length !== 1 ? "s" : ""} available — preview and add them to a campaign`}
    >
      <BlockStack gap="600">
        {sections.map(({ family, games: sectionGames }) => (
          <GameCatalogSection
            key={family.id}
            title={family.title}
            description={family.description}
          >
            {sectionGames.map((game) => renderGameCard(game))}
          </GameCatalogSection>
        ))}

        {unmappedGames.length > 0 ? (
          <GameCatalogSection
            title="Games"
            description="Additional games not yet grouped under a family."
          >
            {unmappedGames.map(renderGameCard)}
          </GameCatalogSection>
        ) : null}

        <Card>
          <Box padding="400">
            <BlockStack align="center">
              <Text as="p" variant="bodySm" tone="subdued">
                More families coming soon: Advent Calendars and more.
              </Text>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default GamesCatalogPage;
